/**
 * File to load the front end asset for the block.
 * Contains functionality to search block.
 *
 * @package
 */

/**
 * External dependencies
 */
/* global searchConfig */
import throttle from 'lodash.throttle';

const initializeSearch = () => {
	const elements = {
		adminBar: document.getElementById( 'wpadminbar' ),
		overlay: document.querySelector( '.overlay' ),
		searchIcons: document.querySelectorAll(
			'.aeblog-search, .aeblog-sticky-nav .aeblog-search',
		),
		searchContainer: document.querySelector( '.aeblog-search-bar' ),
		searchContainerClose: document.querySelector(
			'.aeblog-search-btn-wrap',
		),
		searchInput: document.getElementById( 'search' ),
		emptySearch: document.querySelector( '.js-search-no-alt' ),
		searchIconWrap: document.querySelector( '.search-icon-wrap' ),
		searchResults: document.querySelector( '.aeblog-search-results' ),
		tagCloudRow: document.querySelector( '.aeblog-tag-cloud-row' ),
		loadingText: document.querySelector( '.aeblog-search-result-loading' ),
		resultContainer: document.getElementById( 'js-search-result' ),
		viewAllButton: document.querySelector(
			'.aeblog-search-result .view-all-button',
		),
		foundResultsElement: document.querySelector(
			'.aeblog-search-result .found-results',
		),
	};

	/**
	 * @type {string}
	 * @description Stores the current search term entered by the user.
	 */
	let currentSearchTerm = '';

	/**
	 * Toggles the visibility of the search modal and adjusts its position based on the viewport width and scroll position.
	 *
	 * @param {boolean} show - A boolean indicating whether to show or hide the search modal.
	 */
	const toggleSearchModal = ( show ) => {
		elements.searchContainer.classList.toggle( 'search-bar-active', show );
		elements.overlay.style.display = show ? 'block' : 'none';

		// Disable body scrolling when the search modal is open.
		document.body.style.overflow = show ? 'hidden' : 'auto';

		if ( show && window.innerWidth <= 768 ) {
			const adminBarHeight = elements.adminBar?.offsetHeight || 0;
			const isScrolledPastAdminBar = window.scrollY >= adminBarHeight;
			elements.searchContainer.classList.toggle(
				'search-bar-top',
				isScrolledPastAdminBar,
			);
			elements.searchContainer.classList.toggle(
				'search-bar-below-admin-bar',
				! isScrolledPastAdminBar,
			);
		}

		if ( show && elements.searchInput ) {
			elements.searchInput.focus();
		} else {
			handleEmptySearchBox();
		}
	};

	/**
	 * Handles the scroll event to toggle the search bar's position based on the scroll position.
	 *
	 * When the window width is 768 pixels or less and the search bar is active, this function checks
	 * if the window has been scrolled past the admin bar. It then toggles the search bar's position
	 * between the top of the page and below the admin bar.
	 *
	 * @function handleScroll
	 */
	const handleScroll = () => {
		if (
			window.innerWidth <= 768 &&
			elements.searchContainer.classList.contains( 'search-bar-active' )
		) {
			const adminBarHeight = elements.adminBar?.offsetHeight || 0;
			const isScrolledPastAdminBar = window.scrollY >= adminBarHeight;
			elements.searchContainer.classList.toggle(
				'search-bar-top',
				isScrolledPastAdminBar,
			);
			elements.searchContainer.classList.toggle(
				'search-bar-below-admin-bar',
				! isScrolledPastAdminBar,
			);
		}
	};

	/**
	 * Handles the search string input by the user.
	 *
	 * This function retrieves the value from the search input element, trims any
	 * leading or trailing whitespace, and updates the current search term. It then
	 * updates the UI to reflect the search term. If the search term is less than
	 * 3 characters long, the function exits early. Otherwise, it fetches the search
	 * results for the given search term.
	 *
	 * @function handleSearchString
	 */
	const handleSearchString = () => {
		const searchedString = elements.searchInput.value.trim();
		currentSearchTerm = searchedString;

		updateUIForSearch( searchedString );

		if ( searchedString.length < 3 ) {
			return;
		}

		fetchSearchResults( searchedString );
	};

	/**
	 * Updates the UI elements based on the search string provided.
	 *
	 * @param {string} searchedString - The string that the user has searched for.
	 * @return {void}
	 */
	const updateUIForSearch = ( searchedString ) => {
		const {
			searchIconWrap,
			searchResults,
			tagCloudRow,
			loadingText,
			viewAllButton,
		} = elements;

		if ( ! searchedString ) {
			searchIconWrap.querySelector( '.js-search-no-alt' ).style.display =
				'none';
			searchIconWrap.querySelector(
				'.aeblog-search-icon',
			).style.display = 'inline-block';
			searchResults.style.display = 'none';
			tagCloudRow.style.display = 'block';
			loadingText.style.display = 'none';
			return;
		}

		viewAllButton.style.display = 'none';
		searchIconWrap.querySelector( '.js-search-no-alt' ).style.display =
			'inline-block';
		searchIconWrap.querySelector( '.aeblog-search-icon' ).style.display =
			'none';

		if ( searchedString.length < 3 ) {
			searchResults.style.display = 'none';
			tagCloudRow.style.display = 'block';
			loadingText.style.display = 'none';
			return;
		}

		loadingText.style.display = 'block';
		tagCloudRow.style.display = 'none';
		document.querySelector( '.no-result-found' )?.remove();

		updateSearchResultsDisplay( searchedString );
	};

	/**
	 * Updates the display of search results with the provided search string.
	 *
	 * This function modifies the text content of an element with the class
	 * 'search-result-for-text' to include the provided search string.
	 * It creates a new span element with the class 'searched-term' to
	 * highlight the searched term.
	 *
	 * @param {string} searchedString - The search string to display in the search results.
	 */
	const updateSearchResultsDisplay = ( searchedString ) => {
		const topSearchText = elements.searchResults.querySelector(
			'.search-result-for-text',
		);
		topSearchText.textContent = 'Search results for: ';
		const searchedTerm = document.createElement( 'span' );
		searchedTerm.classList.add( 'searched-term' );
		searchedTerm.textContent = searchedString;
		topSearchText.appendChild( searchedTerm );
	};

	/**
	 * Fetches search results from the custom search URL and displays them.
	 *
	 * @param {string} searchedString - The string to search for.
	 * @return {void}
	 *
	 * @example
	 * fetchSearchResults('example search term');
	 *
	 * @throws Will log an error to the console if the fetch request fails.
	 */
	const fetchSearchResults = ( searchedString ) => {
		fetch(
			`${ searchConfig.customSearchUrl }?s=${ encodeURIComponent(
				searchedString,
			) }`,
			{
				headers: {
					'X-WP-Nonce': searchConfig.restNonce,
				},
			},
		)
			.then( ( response ) => response.json() )
			.then( ( response ) => {
				if ( searchedString !== currentSearchTerm ) {
					return;
				}

				displaySearchResults( response, searchedString );
			} )
			.catch( ( error ) => {
				console.error( 'Error fetching search results:', error );
			} );
	};

	/**
	 * Displays the search results based on the response from the server.
	 *
	 * @param {Object} response             - The response object from the server.
	 * @param {number} response.found_posts - The number of posts found.
	 * @param {Array}  response.data        - The array of post data.
	 * @param {string} [response.message]   - The message to display if no results are found.
	 * @param {string} searchedString       - The string that was searched for.
	 */
	const displaySearchResults = ( response, searchedString ) => {
		const {
			loadingText,
			searchResults,
			resultContainer,
			viewAllButton,
			foundResultsElement,
		} = elements;

		loadingText.style.display = 'none';

		if ( response.found_posts !== undefined ) {
			foundResultsElement.textContent = `${ response.found_posts } Results`;
		}

		if ( response.data && response.data.length > 0 ) {
			searchResults.style.display = 'block';
			resultContainer.innerHTML = response.data // phpcs:ignore WordPressVIPMinimum.JS.InnerHTML.Found
				.map( ( item ) => {
					const postTemplate = wp.template(
						'post-card',
					);
					return postTemplate( item );
				} )
				.join( '' );

			if ( 24 < parseInt( response.found_posts ) ) {
				const link = viewAllButton.querySelector(
					'.aeblog-search-view-all-link',
				);
				link.setAttribute(
					'href',
					`${ searchConfig.homeUrl }/?s=${ encodeURIComponent(
						searchedString,
					) }`,
				);
				viewAllButton.style.display = 'block';
			}
		} else {
			foundResultsElement.textContent = '';
			// phpcs:disable WordPressVIPMinimum.JS.InnerHTML.Found
			resultContainer.innerHTML = `<span class="no-result-found">${
				response.message || 'No results found'
			}</span>`;
			// phpcs:enable WordPressVIPMinimum.JS.InnerHTML.Found
			searchResults.style.display = 'block';
		}
	};

	/**
	 * Handles the behavior of the search box when it is empty.
	 *
	 * This function performs the following actions:
	 * - Clears the value of the search input field.
	 * - Hides the empty search message.
	 * - Displays the search icon.
	 * - Hides the search results.
	 * - Displays the tag cloud row.
	 *
	 * @function handleEmptySearchBox
	 */
	const handleEmptySearchBox = () => {
		const field = document.querySelector(
			'.aeblog-search-fields .search-field',
		);
		if ( field ) {
			field.value = '';
		}
		elements.emptySearch.style.display = 'none';
		document.querySelector( '.aeblog-search-icon' ).style.display =
			'inline-block';
		elements.searchResults.style.display = 'none';
		elements.tagCloudRow.style.display = 'block';
	};

	// Open search modal when clicking search icons in the header.
	elements.searchIcons.forEach( ( icon ) => {
		icon.addEventListener( 'click', () => toggleSearchModal( true ) );
		icon.addEventListener( 'keyup', ( e ) => e.key === 'Enter' && toggleSearchModal( true ) );
	} );

	// Close search modal when clicking close icon and empty the search box.
	elements.searchContainerClose.addEventListener( 'click', () =>
		toggleSearchModal( false ),
	handleEmptySearchBox,
	);

	// Close search modal when clicking outside the modal (on the overlay).
	elements.overlay.addEventListener( 'click', ( event ) => {
		if ( event.target === elements.overlay ) {
			toggleSearchModal( false );
		}
	} );

	// Handle search input with throttling to improve performance.
	elements.searchInput.addEventListener(
		'input',
		throttle( handleSearchString, 500 ),
	);

	// Clear search box when clicking the cross icon within the search input.
	elements.emptySearch.addEventListener( 'click', handleEmptySearchBox );

	// Initial setup
	elements.searchResults.style.display = 'none';
	window.addEventListener( 'scroll', handleScroll );
};

document.addEventListener( 'DOMContentLoaded', initializeSearch );
